import { useNavigate, useParams } from "react-router";
import {
  ClipboardIcon,
  DownloadIcon,
  MoreVerticalIcon,
  UploadIcon,
} from "vega-icons";

import { useGetCoInvestGpPermissions } from "@vega-api/generated/coinvest-gp-permissions/coinvest-gp-permissions";
import { BulkActions } from "@vega-core/components/BulkActions/BulkActions";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { SearchInput } from "@vega-core/components/Form/Controls/Input/SearchInput/SearchInput";
import { InformativeModal } from "@vega-core/components/Modals/InformativeModal/InformativeModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@vega-core/components/shadcn/dropdown-menu";
import { Switch } from "@vega-core/components/Switch/Switch";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { m } from "@vega-core/paraglide/messages";
import { downloadBlobAsFile } from "@vega-core/utils/download.helpers";
import { getIsGPCoInvestPortal } from "@vega-core/utils/global";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CoInvestmentFullPaths, CommonPaths } from "@vega-modules/paths";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { useProspectsExportColumns } from "../components/ProspectsListTable/hooks/useProspectsExportColumns";
import { useProspectsListManagement } from "../components/ProspectsListTable/hooks/useProspectsListManagement";
import { ProspectsListTable } from "../components/ProspectsListTable/ProspectsListTable";
import { NotesModal } from "../components/ProspectsTable/components/NotesModal";
import {
  OPPORTUNITIES_CSV_UPLOAD_INPUT_ID,
  OPPORTUNITIES_UPLOAD_TEMPLATE_CONTENT,
  OPPORTUNITIES_UPLOAD_TEMPLATE_FILENAME,
  PROSPECTS_CSV_UPLOAD_INPUT_ID,
  PROSPECTS_UPLOAD_TEMPLATE_CONTENT,
  PROSPECTS_UPLOAD_TEMPLATE_FILENAME,
} from "../constants/prospectsUpload";
import { useDealName } from "../hooks/useDealName";
import { useExportFilename } from "../hooks/useExportFilename";
import { useTableExport } from "../hooks/useTableExport";

export const ProspectsPage = () => {
  useFeatureFlagGuard(
    "gp_co_investment",
    // Only redirect to home when not in GP co-invest portal
    getIsGPCoInvestPortal() ? undefined : CommonPaths.Home,
  );
  const navigate = useNavigate();
  const { id: dealId = "" } = useParams<{ id: string }>();

  const { dealName, isLoading: isDealNameLoading } = useDealName(dealId);

  const { data: permissions } = useGetCoInvestGpPermissions(
    { dealId },
    {
      query: {
        select: (response) => response.data,
      },
    },
  );
  const canApproveDealShortlist = permissions?.canFinalizeDealShortlist;
  const {
    prospectsData,
    canAddShortlistItems,
    canEditShortlistValues,
    isLoading,
    isProspectsListTableLoading,
    paginationElement,
    searchQuery,
    setSearchQuery,
    showOnlyAddedByMe,
    setShowOnlyAddedByMe,
    showOnlyApproved,
    setShowOnlyApproved,
    showRemoveConfirmation,
    selectedProspectForRemoval,
    rowSelection,
    selectedCount,
    showApprovalModal,
    handleProspectsFileUpload,
    handleProspectsUploadClick,
    handleRemoveIndividualProspect,
    handleShowIndividualRemoveConfirmation,
    handleApproveProspects,
    handleApproveSingleProspect,
    approvingProspectId,
    closeRemoveConfirmation,
    closeApprovalModal,
    openApprovalModal,
    handleRowSelectionChange,
    clearSelection,
    showSelectedOnly,
    handleShowSelectedOnlyChange,
    isNotesModalOpen,
    selectedProspectNotes,
    closeNotesModal,
    handleOpenNotesModal,
    handleOpportunitiesFileUpload,
    handleOpportunitiesUploadClick,
  } = useProspectsListManagement();

  const exportColumns = useProspectsExportColumns();
  const exportFilename = useExportFilename(dealId, "prospects");

  const { exportToCsv, exportToExcel, isExporting } = useTableExport({
    data: prospectsData,
    columns: exportColumns,
    filename: exportFilename,
  });

  const handleFindProspectsFromSalesforce = () => {
    navigate(
      CoInvestmentFullPaths.CoInvestmentProspectsFindFromSalesforce.replace(
        ":id",
        dealId,
      ),
    );
  };

  const handleDownloadProspectsCsvTemplate = async () => {
    const blob = new Blob([PROSPECTS_UPLOAD_TEMPLATE_CONTENT], {
      type: "text/csv;charset=utf-8;",
    });
    downloadBlobAsFile(blob, PROSPECTS_UPLOAD_TEMPLATE_FILENAME);
  };

  const handleDownloadOpportunitiesCsvTemplate = async () => {
    const blob = new Blob([OPPORTUNITIES_UPLOAD_TEMPLATE_CONTENT], {
      type: "text/csv;charset=utf-8;",
    });
    downloadBlobAsFile(blob, OPPORTUNITIES_UPLOAD_TEMPLATE_FILENAME);
  };

  return (
    <>
      <div className="mt-1 flex flex-col gap-4">
        <CoInvestmentPageHeader
          action={
            canAddShortlistItems ? (
              <div className="flex gap-4 items-center flex-wrap">
                <ThemedButton
                  dataTestId="find-prospects-salesforce-button"
                  disabled={isLoading}
                  onClick={handleFindProspectsFromSalesforce}
                  size="small"
                  variant="primary"
                >
                  {m.coInvestments_findProspectsFromSalesforce()}
                </ThemedButton>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <ThemedButton
                      ariaLabel="More actions"
                      className="h-10 w-10"
                      dataTestId="prospects-actions-menu"
                      disabled={isLoading || isExporting}
                      icon={<MoreVerticalIcon />}
                      variant="secondary"
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="p-2">
                    <DropdownMenuItem onSelect={handleProspectsUploadClick}>
                      <UploadIcon className="w-4 h-4" />
                      <p className="body-base-medium">
                        {m.coInvestments_uploadProspectsCsv()}
                      </p>
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={handleOpportunitiesUploadClick}>
                      <UploadIcon className="w-4 h-4" />
                      <p className="body-base-medium">
                        {m.coInvestments_uploadOpportunitiesCsv()}
                      </p>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={handleDownloadProspectsCsvTemplate}
                    >
                      <ClipboardIcon className="w-4 h-4" />
                      <p className="body-base-medium">
                        {m.coInvestments_downloadProspectsTemplate()}
                      </p>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={handleDownloadOpportunitiesCsvTemplate}
                    >
                      <ClipboardIcon className="w-4 h-4" />
                      <p className="body-base-medium">
                        {m.coInvestments_downloadOpportunitiesTemplate()}
                      </p>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={exportToCsv}>
                      <DownloadIcon className="w-4 h-4" />
                      <p className="body-base-medium">{m.btn_exportAsCsv()}</p>
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={exportToExcel}>
                      <DownloadIcon className="w-4 h-4" />
                      <p className="body-base-medium">
                        {m.btn_exportAsExcel()}
                      </p>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : undefined
          }
          capitalize={false}
          description={
            <p
              className="text-text/secondary body-base-regular"
              data-testid="prospects-page-description"
            >
              {m.coInvestments_prospectsDescription()}
            </p>
          }
          fundName={dealName}
          isLoading={isLoading || isDealNameLoading}
          title={m.coInvestments_nav_prospects()}
        />
        <div className="flex gap-6">
          {[
            {
              checked: showOnlyAddedByMe,
              label: m.coInvestments_prospects_addedByMe(),
              onChange: setShowOnlyAddedByMe,
            },
            {
              checked: showOnlyApproved,
              label: m.coInvestments_prospects_approvedOnly(),
              onChange: setShowOnlyApproved,
            },
          ].map(({ checked, label, onChange }) => (
            <Tooltip
              content={
                showSelectedOnly
                  ? m.coInvestments_prospects_filterDisabledWhileShowingSelected()
                  : undefined
              }
              key={label}
            >
              <span>
                <Switch
                  checked={checked}
                  disabled={showSelectedOnly}
                  label={label}
                  labelClassName="text-sm"
                  onChange={onChange}
                />
              </span>
            </Tooltip>
          ))}
          {selectedCount > 0 ? (
            <Switch
              checked={showSelectedOnly}
              label={m.dataGrid_showSelectedOnly()}
              labelClassName="text-sm"
              onChange={handleShowSelectedOnlyChange}
            />
          ) : null}
        </div>
        <SearchInput
          className="max-w-md"
          disabled={showSelectedOnly}
          name="prospects-search"
          onChange={(e) => setSearchQuery(e.target.value)}
          value={searchQuery}
          withBorder
        />
        <div className="mt-2 mb-48">
          <input
            accept=".csv"
            className="hidden"
            data-testid={PROSPECTS_CSV_UPLOAD_INPUT_ID}
            disabled={isLoading}
            id={PROSPECTS_CSV_UPLOAD_INPUT_ID}
            onChange={handleProspectsFileUpload}
            type="file"
          />
          <input
            accept=".csv"
            className="hidden"
            data-testid={OPPORTUNITIES_CSV_UPLOAD_INPUT_ID}
            disabled={isLoading}
            id={OPPORTUNITIES_CSV_UPLOAD_INPUT_ID}
            onChange={handleOpportunitiesFileUpload}
            type="file"
          />
          <div className="space-y-4">
            <ProspectsListTable
              approvingProspectId={approvingProspectId}
              canApprove={canApproveDealShortlist}
              canEdit={canEditShortlistValues}
              dealId={dealId}
              isLoading={isProspectsListTableLoading}
              onApproveProspect={handleApproveSingleProspect}
              onFindFromSalesforce={handleFindProspectsFromSalesforce}
              onOpenNotesModal={handleOpenNotesModal}
              onRemoveProspect={handleShowIndividualRemoveConfirmation}
              onRowSelectionChange={handleRowSelectionChange}
              onUploadClick={handleProspectsUploadClick}
              paginationElement={paginationElement}
              prospectsData={prospectsData}
              removingProspectId={selectedProspectForRemoval}
              rowSelection={rowSelection}
            />
          </div>
        </div>
      </div>

      <InformativeModal
        description={m.coInvestments_prospects_remove_confirmModal_description()}
        isOpen={showRemoveConfirmation}
        onClose={closeRemoveConfirmation}
        primaryBtnProps={{
          children: m.btn_continue(),
          onClick: () => {
            if (selectedProspectForRemoval) {
              handleRemoveIndividualProspect(selectedProspectForRemoval);
            }
          },
          variant: "primary",
        }}
        secondaryBtnProps={{
          children: m.btn_cancel(),
          onClick: closeRemoveConfirmation,
        }}
        title={m.coInvestments_prospects_remove_confirmModal_title()}
        variant="error"
      />

      <InformativeModal
        description={m.coInvestments_approveProspectList_description()}
        isOpen={showApprovalModal}
        onClose={closeApprovalModal}
        primaryBtnProps={{
          children: m.btn_continue(),
          onClick: handleApproveProspects,
          variant: "primary",
        }}
        secondaryBtnProps={{
          children: m.btn_cancel(),
          onClick: closeApprovalModal,
        }}
        title={m.coInvestments_approveProspectList_title()}
        variant="success"
      />

      {isNotesModalOpen && selectedProspectNotes ? (
        <NotesModal
          canEdit={canEditShortlistValues}
          dealId={dealId}
          isOpen={isNotesModalOpen}
          lpName={selectedProspectNotes.lpName}
          notes={selectedProspectNotes.notes}
          onClose={closeNotesModal}
          shortlistItemId={selectedProspectNotes.shortlistItemId}
        />
      ) : null}

      <BulkActions
        isLoading={isLoading}
        onClear={clearSelection}
        primaryAction={
          canApproveDealShortlist
            ? {
                children: m.coInvestments_approveProspectList(),
                onClick: openApprovalModal,
                variant: "primary",
              }
            : undefined
        }
        selectedCount={selectedCount}
        selectionLabel={{
          single: m.coInvestments_prospect(),
          multiple: m.coInvestments_prospects(),
        }}
      />
    </>
  );
};
